import { useRef, type ComponentProps } from 'react'
import { useBlocker } from '@tanstack/react-router'

import { ApiError } from '@/lib/api'
import { Button } from '@/ui/button'
import { ConfirmDialog } from '@/ui/confirm-dialog'

export function printerPolicyDeleteError(error: unknown) {
  if (error instanceof ApiError) {
    if (error.status === 403) return 'You no longer have permission to delete this policy. Cancel and ask an administrator to review your access.'
    if (error.status === 409) return 'This policy changed. Reload policies and review the latest version before deleting it.'
    if (error.status === 404) return 'This policy no longer exists. Reload policies to update the list.'
  }
  return 'Couldn’t confirm deletion. Reload policies to check whether it succeeded before trying again.'
}

export function PrinterPolicyDeleteDialog({ name, description, canDelete, onClose, onConfirm, onReload, mutation, finalFocus }: {
  name: string
  description: string
  canDelete: boolean
  onClose: () => void
  onConfirm: () => void
  onReload: () => Promise<void>
  mutation: ComponentProps<typeof ConfirmDialog>['mutation']
  finalFocus: ComponentProps<typeof ConfirmDialog>['finalFocus']
}) {
  const submitted = useRef(false)
  useBlocker({
    shouldBlockFn: ({ current, next }) => mutation.isPending && current.pathname !== next.pathname,
    enableBeforeUnload: mutation.isPending,
  })
  return <ConfirmDialog
    open
    finalFocus={finalFocus}
    onOpenChange={(open) => { if (!open && !mutation.isPending) onClose() }}
    title={`Delete ${name}?`}
    description={description}
    confirmLabel="Delete policy"
    pendingLabel="Deleting…"
    confirmDisabled={!canDelete || mutation.isError}
    mutation={mutation}
    errorMessage={printerPolicyDeleteError}
    details={<>
      {!canDelete && <p role="alert" className="text-destructive">You no longer have permission to delete this policy.</p>}
      {mutation.isError && <Button variant="outline" size="sm" onClick={() => {
        onClose()
        void onReload()
      }}>Reload policies</Button>}
    </>}
    onConfirm={() => {
      if (!canDelete || mutation.isPending || mutation.isError || submitted.current) return
      submitted.current = true
      onConfirm()
    }}
  />
}
